import * as THREE from "three";
import { calculateHeaterPlacement } from "../domain/chimney/calculateHeaterPlacement";
import { buildInteriorPanelCutRow } from "../domain/finishes/interiorPanelCuts";
import { DEFAULT_FINISHES, type FinishConfig } from "../domain/finishes/types";
import { calculateFoundation } from "../domain/foundation/calculateFoundation";
import {
	DEFAULT_SITE_CONDITIONS,
	type SiteConditions,
} from "../domain/foundation/siteConditions";
import type { WallOpening } from "../domain/framing/generateFramedWall";
import { calculateWallStudCuts } from "../domain/framing/wallStudCuts";
import {
	type HeaterRef,
	heaterKindOf,
	heaterModelIdOf,
	resolveWallHeightM,
} from "../domain/geometry/buildingDimensions";
import {
	freeWalls,
	type IndoorRoomConfig,
	normalizeIndoorRoom,
} from "../domain/indoor/indoorRoom";
import {
	createInteriorLayout,
	DEFAULT_HEATER_WALL_GAP_MM,
	type HeaterPlacement,
	type InteriorRoom,
	type InteriorWall,
	reconcileInteriorLayout,
} from "../domain/interior";
import type { BenchMaterialsResult } from "../domain/interior/deriveBenchMaterials";
import { resolveHingeSide } from "../domain/openings/openingPresets";
import { createRectRoofGeometry } from "../domain/roof/rectRoofGeometry";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import type { RoofJoistResult } from "../domain/snowzone/calculateRoofJoists";
import { calculateWeight } from "../domain/weight/calculateWeight";
import { createCameraPosition, createCameraPreset } from "./cameraFraming";
import { type ChimneyBuildInput, chimneyStackFor } from "./chimneyInput";
import {
	ENGINE_CC_M as CC,
	ENGINE_CLADDING_COV_M as CLAD_COV,
	ENGINE_CLADDING_T_M as CLAD_T,
	ENGINE_FURRING_D_M as FURR_D,
	ENGINE_JOIST_H_M as JOIST_H,
} from "./constants";
import { getDeckSuppressedWalls } from "./deckGeometry";
import { addWallVent } from "./detailHelpers";
import { DirtyFlag } from "./dirtyTracking";
import {
	calculateVentilatedFacadeQuantities,
	defaultStudLinesByWall,
} from "./facadeQuantities";
import {
	createFrontVisibilityGroup,
	type FrontVisibilityGroups,
} from "./frontVisibility";
import { addBox } from "./geometry";
import {
	getHeaterSizeMm as getEngineHeaterSizeMm,
	getHeaterHeight,
	getHeaterWidth,
} from "./heaterGeometry";
import { calculateInteriorLiningQuantities } from "./interiorQuantities";
import {
	createInteriorRenderPlan,
	heaterDoorRotationY,
	type InteriorRenderPlan,
} from "./interiorRenderPlan";
import { matDivider, matDoorHandle, matGlass } from "./materials";
import type { PremiumDetailPlacement } from "./premiumDetails";
import {
	getFloorJoistDimensionLabel,
	getFloorJoistPriceRuleId,
	getRoofJoistCutDimensionLabel,
	getRoofJoistCutPurchase,
	getRoofJoistDepthM,
	getRoofJoistDepthMm,
	getRoofJoistDimensionLabel,
	getRoofJoistMaterialName,
	getRoofJoistPriceRuleId,
	getStructuralRoofJoists,
} from "./roofJoistSizing";
import {
	buildExteriorFacade,
	buildFloorJoists,
	buildFoundation,
	buildIndoorOuterLayers,
	buildInteriorCeilingFinish,
	buildInteriorCeilingLayers,
	buildPanels,
	buildRoof,
	buildSaunaRoomCore,
	buildWallFraming,
	createSquareBaseBuildContext,
	getFrontDoorCenterX,
	type SquareBaseBuildContext,
	type WallFramingResult,
} from "./squareBaseBuilder";
import type { FlueAxisM } from "./squareBaseBuilder/context";
import {
	resolveSubfloorBattThicknessMm,
	resolveSubfloorBayCount,
	resolveSubfloorLedgerLm,
} from "./subfloor";
import type {
	BuildInput,
	BuildResult,
	CutRow,
	HeaterType,
	MaterialRow,
	RawCalculationResult,
	RoofConfig,
	SaunaSiting,
} from "./types";

export type { SaunaDimensions, VisibilityFlags } from "./types";
export type {
	BuildResult,
	CutRow,
	MaterialRow,
	RawCalculationResult as CalculationResult,
};

const PREMIUM_FRONT_DOOR_ASSET_PATH = "/assets/premium/square/front-door.glb";
const PREMIUM_FRONT_DOOR_BASE_WIDTH = 0.7;
const PREMIUM_FRONT_DOOR_BASE_HEIGHT = 1.9;

export function createSquareBuildContext(
	W: number,
	L: number,
	roofConfig: RoofConfig = DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig,
	roofJoists?: RoofJoistResult,
	wallHeightM?: number,
) {
	return createSquareBaseBuildContext(W, L, {
		roofConfig,
		roofJoistDepthM: getRoofJoistDepthM(roofJoists),
		wallHeightM,
	});
}

function addPremiumFrontDoorFallbacks(
	ctx: SquareBaseBuildContext,
	framingResults: Map<string, WallFramingResult>,
	frontExteriorGroup: THREE.Group,
): PremiumDetailPlacement[] {
	const frontOpenings = framingResults.get("front")?.wallOpenings ?? [];
	const placements: PremiumDetailPlacement[] = [];

	for (const opening of frontOpenings) {
		if (opening.type !== "door") continue;

		const width = opening.widthMm / 1000;
		const height = opening.heightMm / 1000;
		const fallbackObjectName = `detail/procedural-door/${opening.id}`;
		const doorCenterX = -ctx.W / 2 + opening.xMm / 1000 + width / 2;
		const doorCenterY = ctx.wallBase + opening.yMm / 1000 + height / 2;
		const doorCenterZ = ctx.L / 2 - ctx.sD - ctx.iL - ctx.pT / 2 + 0.015;

		const fallbackRoot = new THREE.Group();
		fallbackRoot.name = fallbackObjectName;
		fallbackRoot.position.set(doorCenterX, doorCenterY, doorCenterZ);
		frontExteriorGroup.add(fallbackRoot);

		// Issue 160: the leaf is what the user picked. A trädörr used to draw
		// as glass here AND load the glass GLB below, while its glass never
		// reached the kW sizing — the scene showed a door the numbers ignored.
		const glazed = opening.glazed === true;
		addBox(
			width * 0.92,
			height * 0.96,
			0.035,
			glazed ? matGlass : matDivider,
			0,
			0,
			0,
			fallbackRoot,
		).name = `detail/procedural-door-leaf/${opening.id}`;

		// The front wall is seen from +Z, where −X is the viewer's left: a
		// left-hung door has its hinges at −X and its handle at +X.
		const handleSign = resolveHingeSide(opening) === "left" ? 1 : -1;
		const handle = new THREE.Mesh(
			new THREE.SphereGeometry(0.02, 10, 10),
			matDoorHandle,
		);
		handle.position.set(handleSign * (width * 0.5 - 0.08), 0, 0.03);
		handle.castShadow = true;
		handle.receiveShadow = true;
		handle.name = `detail/procedural-door-handle/${opening.id}`;
		fallbackRoot.add(handle);

		// The GLB is a glass door with its handle at +X; there is no wooden
		// asset, so a trädörr keeps the procedural leaf.
		if (!glazed) continue;
		placements.push({
			id: "square-front-door",
			openingId: opening.id,
			assetPath: PREMIUM_FRONT_DOOR_ASSET_PATH,
			targetGroup: "exterior",
			position: fallbackRoot.position.clone(),
			rotation: new THREE.Euler(0, 0, 0),
			// Mirrored for a right-hung door; three.js flips the face winding
			// of a negative-determinant object, so the mirror renders solid.
			scale: new THREE.Vector3(
				(handleSign * width) / PREMIUM_FRONT_DOOR_BASE_WIDTH,
				height / PREMIUM_FRONT_DOOR_BASE_HEIGHT,
				1,
			),
			fallbackObjectName,
		});
	}

	return placements;
}

function buildSquareExteriorVents(
	ctx: SquareBaseBuildContext,
	exteriorGroup: THREE.Group,
	frontExteriorGroup: THREE.Group,
	wbThick: number,
) {
	const innerW = ctx.W - 2 * ctx.sD - 2 * ctx.iL - 2 * ctx.pT;
	const heaterW = 0.4;
	const hX = innerW / 2 - heaterW / 2 - 0.15;

	// Intake: on front wall, directly aligned with heater X, low (15 cm above floor).
	// Mounted on the cladding face so it isn't hidden behind facade boards.
	// Facade build-up = wind barrier + ströläkt + spikläkt (korsläkt) + panel.
	const cladOuterFront = ctx.L / 2 + wbThick + 2 * FURR_D + CLAD_T;
	addWallVent({
		group: frontExteriorGroup,
		position: new THREE.Vector3(hX, ctx.wallBase + 0.15, cladOuterFront),
		facing: "+z",
		kind: "supply",
		name: "square-intake",
	});

	// Exhaust: on back wall, opposite corner from heater, high up (20 cm from ceiling).
	const cladOuterBack = -ctx.L / 2 - (wbThick + 2 * FURR_D + CLAD_T);
	addWallVent({
		group: exteriorGroup,
		position: new THREE.Vector3(
			-ctx.W / 2 + ctx.sD + ctx.iL + ctx.pT + 0.06,
			ctx.wallBase + ctx.H - 0.2,
			cladOuterBack,
		),
		facing: "-z",
		kind: "exhaust",
		name: "square-exhaust",
	});
}

/**
 * The room rectangle the interior layout is measured in — the finished (inner)
 * faces of the four walls. Mirrors `deriveHeaterClearanceLayout`'s
 * `roomInnerBox`, which is where the layout resolver reads it from.
 */
export function getSquareInteriorRoom(
	ctx: SquareBaseBuildContext,
): InteriorRoom {
	const innerW = ctx.W - 2 * ctx.sD - 2 * ctx.iL - 2 * ctx.pT;
	const innerL = ctx.L - 2 * ctx.sD - 2 * ctx.iL - 2 * ctx.pT;
	return {
		id: "sauna",
		minXMm: (-innerW / 2) * 1000,
		maxXMm: (innerW / 2) * 1000,
		minZMm: (-innerL / 2) * 1000,
		maxZMm: (innerL / 2) * 1000,
	};
}

/**
 * Where this engine stands the aggregat, expressed in layout coordinates.
 *
 * The registry resolves the interior layout for every build, so in practice the
 * plan always arrives from outside. This is the fallback for a direct call, and
 * it is also what tells the plan whether the user has MOVED the unit — see
 * `HeaterRenderPlacement.atGeneratedPlacement`.
 */
function getSquareHeaterPlacement(
	ctx: SquareBaseBuildContext,
	heater: HeaterRef,
	heaterPlacementZMm: number | undefined,
	room: InteriorRoom,
): HeaterPlacement {
	const placement = calculateHeaterPlacement({
		saunaType: "fyrkantig",
		widthM: ctx.W,
		lengthM: ctx.L,
		heaterType: heaterKindOf(heater),
		heaterModelId: heaterModelIdOf(heater),
		roofKind: ctx.roofConfig.kind,
		preferredCenterZMm: heaterPlacementZMm,
	});
	return {
		wall: "right",
		offsetMm: placement.selectedCenterZMm - room.minZMm,
		wallGapMm: DEFAULT_HEATER_WALL_GAP_MM,
	};
}

/**
 * The interior a square sauna is drawn with: the resolved layout when the
 * registry hands one down, otherwise the generated default. One owner, so the
 * roof (whose joists are trimmed around the flue) and the interior (which
 * stands the stove under it) read the same heater.
 */
function resolveSquareInteriorPlan(
	ctx: SquareBaseBuildContext,
	heaterType: HeaterRef,
	heaterPlacementZMm: number | undefined,
	interior: InteriorRenderPlan | undefined,
): InteriorRenderPlan {
	if (interior) return interior;
	const room = getSquareInteriorRoom(ctx);
	const heaterSize = getEngineHeaterSizeMm(heaterType);
	const generatedHeater = getSquareHeaterPlacement(
		ctx,
		heaterType,
		heaterPlacementZMm,
		room,
	);
	// A design with no authored layout still goes through the layout, not around
	// it: `createInteriorLayout` is the same #130 generator the BOM bills from,
	// so "default" is one description of the sauna rather than two that agree.
	return createInteriorRenderPlan({
		layout: reconcileInteriorLayout(
			createInteriorLayout(room, { heater: generatedHeater }),
			[room],
			heaterSize,
		),
		rooms: [room],
		heaterSize,
		generatedHeater,
		namePrefix: "square",
	});
}

/** Where the drawn chimney rises: over a wood stove, nowhere otherwise. */
function squareFlueAxis(
	heaterType: HeaterRef,
	plan: InteriorRenderPlan,
): FlueAxisM | null {
	return heaterKindOf(heaterType) === "wood"
		? { x: plan.heater.centerXM, z: plan.heater.centerZM }
		: null;
}

function buildSquareInterior(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	frontInteriorGroup: THREE.Group,
	framingResults: Map<"front" | "back" | "left" | "right", WallFramingResult>,
	heaterType: HeaterRef = "wood",
	heaterPlacementZMm: number | undefined,
	interior: InteriorRenderPlan | undefined,
	panelDirection: FinishConfig["panelDirection"] = DEFAULT_FINISHES.panelDirection,
	chimneyInput: ChimneyBuildInput,
) {
	// The ceiling's hidden layers (isolering, folie, takgles) belong to the
	// INTERIOR group like the walls' do, so hiding the panel reveals them
	// (issue 115). The bastupanel + taklist stay in the panel group and are
	// built by `buildInteriorCeilingFinish`.
	const heaterW = getHeaterWidth(heaterType);
	const room = getSquareInteriorRoom(ctx);
	const plan = resolveSquareInteriorPlan(
		ctx,
		heaterType,
		heaterPlacementZMm,
		interior,
	);
	buildInteriorCeilingLayers(
		ctx,
		interiorGroup,
		"square",
		squareFlueAxis(heaterType, plan),
	);

	const heaterCenterX = plan.heater.centerXM;
	const heaterCenterZ = plan.heater.centerZM;
	buildSaunaRoomCore(ctx, interiorGroup, frontInteriorGroup, {
		namePrefix: "square",
		panelDirection,
		framingResults,
		heaterType,
		// The quote's stack, measured over the roof where this stove's flue
		// passes it (`withChimney` reads the same layout heater).
		chimneyStack: chimneyStackFor(
			chimneyInput,
			squareFlueAxis(heaterType, plan),
		),
		benches: plan.benches,
		heater: new THREE.Vector3(
			heaterCenterX,
			// Rest the heater on the floor regardless of type — the electric
			// tower is 0.9m tall, so a fixed wood-height offset sank it ~90mm
			// into the floor joists.
			ctx.wallBase + getHeaterHeight(heaterType) / 2 + 0.005,
			heaterCenterZ,
		),
		// Math.PI on a long wall = door faces into the room, away from the front
		// wall. A short-wall placement (only reachable by authoring one) has to
		// open away from ITS wall instead, or the unit is drawn facing the panel.
		heaterRotationY: heaterDoorRotationY(plan.heater.wall) ?? Math.PI,
		frontShield: {
			group: frontInteriorGroup,
			position: new THREE.Vector3(
				heaterCenterX,
				ctx.wallBase + 0.55,
				heaterCenterZ + shieldStandoffZ(plan.heater.wall, heaterW),
			),
		},
		// The side shield hangs on the wall the unit backs onto — any of the four
		// once a layout may move it, not just the two long ones.
		sideShield: shieldWallPosition(
			plan.heater.wall,
			ctx.wallBase + 0.55,
			room,
			heaterCenterX,
			heaterCenterZ,
		),
		sideShieldWall: plan.heater.wall,
	});
}

/**
 * Standoff of the freestanding front shield from the heater centre, along Z.
 *
 * It protects the +Z direction on a long-wall placement (where the door faces
 * along the room). On a SHORT wall the unit's own wall is a Z face, so the plate
 * has to move to the side the door actually opens onto — otherwise it is drawn
 * inside the wall it is supposed to stand clear of.
 */
function shieldStandoffZ(wall: InteriorWall, heaterW: number): number {
	const offset = heaterW / 2 + 0.12;
	return wall === "front" ? -offset : offset;
}

/** Position for the shield plate on the wall the unit backs onto. */
function shieldWallPosition(
	wall: InteriorWall,
	y: number,
	room: InteriorRoom,
	heaterCenterX: number,
	heaterCenterZ: number,
): THREE.Vector3 {
	switch (wall) {
		case "left":
			return new THREE.Vector3(room.minXMm / 1000, y, heaterCenterZ);
		case "right":
			return new THREE.Vector3(room.maxXMm / 1000, y, heaterCenterZ);
		case "back":
			return new THREE.Vector3(heaterCenterX, y, room.minZMm / 1000);
		case "front":
			return new THREE.Vector3(heaterCenterX, y, room.maxZMm / 1000);
	}
}

/**
 * Cross-cutting build inputs the square engine's positional signature would
 * otherwise have to grow a tail for.
 *
 * `interior` is the resolved interior layout — the registry passes it for
 * every real build; omitting it falls back to the generated default, built
 * through the same layout code path.
 *
 * `finishes` is the karaktärsval. Only `panelDirection` reaches the geometry
 * (it selects the bastupanel's material: standing vs 90°-rotated texture);
 * species and facade colour restyle SHARED materials via
 * `applyFinishSelection`, so they never need a rebuild.
 */
export interface SquareBuildOptions {
	interior?: InteriorRenderPlan;
	finishes?: FinishConfig;
	/** `BuildInput.wallHeightM` (issue 155). */
	wallHeightM?: number;
	/** `BuildInput.siting` (issue 156). */
	siting?: SaunaSiting;
	/** `BuildInput.indoorRoom` (issue 174); only read indoors. */
	indoorRoom?: IndoorRoomConfig;
	/**
	 * What the chimney height reads. The registry hands down the build input
	 * `withChimney` quotes from (it carries `BuildInput.overrides`); direct
	 * callers get these arguments.
	 */
	chimneyInput?: ChimneyBuildInput;
}

export function buildSauna3D(
	W: number,
	L: number,
	openings: WallOpening[] = [],
	roofConfig: RoofConfig = DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig,
	siteConditions: SiteConditions = DEFAULT_SITE_CONDITIONS,
	deckConfig: BuildInput["deckConfig"] = null,
	heaterType: HeaterRef = "wood",
	roofJoists?: RoofJoistResult,
	heaterPlacementZMm?: number,
	/**
	 * The cross-cutting choices, as a bag rather than a growing positional tail
	 * — every new one would otherwise append another optional arg to a
	 * signature that already needs `undefined` placeholders, and drift further
	 * from the other two engines, which read them off `BuildInput`.
	 */
	opts: SquareBuildOptions = {},
): BuildResult {
	const { interior } = opts;
	const finishes = opts.finishes ?? DEFAULT_FINISHES;
	const ctx = createSquareBuildContext(
		W,
		L,
		roofConfig,
		roofJoists,
		opts.wallHeightM,
	);
	const entryDoorCenterX = getFrontDoorCenterX(ctx, openings);
	const foundation = calculateFoundation(W, L, siteConditions);

	const saunaGroup = new THREE.Group();
	const foundationGroup = new THREE.Group();
	const deckGroup = new THREE.Group();
	const framingGroup = new THREE.Group();
	const panelGroup = new THREE.Group();
	const roofGroup = new THREE.Group();
	const interiorGroup = new THREE.Group();
	const exteriorGroup = new THREE.Group();
	const frontVisibilityGroups: FrontVisibilityGroups = {
		framing: createFrontVisibilityGroup("framing", framingGroup),
		panel: createFrontVisibilityGroup("panel", panelGroup),
		roof: createFrontVisibilityGroup("roof", roofGroup),
		interior: createFrontVisibilityGroup("interior", interiorGroup),
		exterior: createFrontVisibilityGroup("exterior", exteriorGroup),
	};

	// Issue 156: an indoor sauna stands on the existing floor — no plintar,
	// bärlinor or entry steps. The floor joists below still carry the
	// insulated sauna floor; they rest on the slab where the beams would be.
	const indoor = opts.siting === "indoor";
	if (!indoor) {
		buildFoundation(ctx, foundation, foundationGroup, {
			namePrefix: "square",
			includeEntrySteps: true,
			entryCenterXM: entryDoorCenterX,
			siteConditions,
			deckConfig,
		});
	}
	buildFloorJoists(ctx, framingGroup, panelGroup, { namePrefix: "square" });
	const framingResults = buildWallFraming(
		ctx,
		openings,
		framingGroup,
		frontVisibilityGroups.framing!,
		{ namePrefix: "square" },
	);
	buildPanels(ctx, framingResults, panelGroup, frontVisibilityGroups.panel!, {
		namePrefix: "square",
		mode: "striped",
		panelDirection: finishes.panelDirection,
	});
	buildInteriorCeilingFinish(
		ctx,
		panelGroup,
		"square",
		frontVisibilityGroups.panel!,
	);
	const premiumPlacements = addPremiumFrontDoorFallbacks(
		ctx,
		framingResults as Map<string, WallFramingResult>,
		frontVisibilityGroups.exterior!,
	);
	// Indoors the outside of the wall is the existing room: no vindskydd,
	// läkt or facade vents, and the ceiling joists carry no roof. What the room
	// sees instead is `buildIndoorOuterLayers` (issue 174).
	if (indoor) {
		buildIndoorOuterLayers(
			ctx,
			framingResults,
			exteriorGroup,
			frontVisibilityGroups.exterior!,
			{
				namePrefix: "square",
				existingWalls: normalizeIndoorRoom(opts.indoorRoom).existingWalls,
			},
		);
	} else {
		const { wbThick } = buildExteriorFacade(
			ctx,
			framingResults,
			exteriorGroup,
			frontVisibilityGroups.exterior!,
			{
				namePrefix: "square",
				mode: "ventilated",
				baseTrimSuppressedWalls: getDeckSuppressedWalls(deckConfig),
			},
		);
		buildSquareExteriorVents(
			ctx,
			exteriorGroup,
			frontVisibilityGroups.exterior!,
			wbThick,
		);
	}
	buildRoof(ctx, roofGroup, frontVisibilityGroups.roof!, {
		namePrefix: "square",
		style: indoor ? "ceiling" : "sloped-detailed",
		flue: squareFlueAxis(
			heaterType,
			resolveSquareInteriorPlan(ctx, heaterType, heaterPlacementZMm, interior),
		),
	});
	buildSquareInterior(
		ctx,
		interiorGroup,
		frontVisibilityGroups.interior!,
		framingResults,
		heaterType,
		heaterPlacementZMm,
		interior,
		finishes.panelDirection,
		opts.chimneyInput ?? {
			dimensions: { width: W, length: L },
			roofConfig,
			wallHeightM: opts.wallHeightM,
			siting: opts.siting,
			indoorRoom: opts.indoorRoom,
		},
	);

	saunaGroup.add(
		foundationGroup,
		framingGroup,
		panelGroup,
		roofGroup,
		interiorGroup,
		exteriorGroup,
	);
	if (deckGroup.children.length > 0) {
		saunaGroup.add(deckGroup);
	}

	return {
		saunaGroup,
		foundationGroup,
		deckGroup,
		framingGroup,
		panelGroup,
		roofGroup,
		interiorGroup,
		exteriorGroup,
		frontVisibilityGroups,
		premiumPlacements,
	};
}

export function buildSquareSubsystem(
	subsystem: DirtyFlag,
	input: {
		dimensions: { width: number; length: number };
		openings: WallOpening[];
		roofConfig?: RoofConfig;
		siteConditions?: SiteConditions;
		thermalInputs?: {
			heaterType?: HeaterType;
			heaterModelId?: string | null;
			heaterPlacementZMm?: number;
		};
		deckConfig?: BuildInput["deckConfig"];
		finishes?: FinishConfig;
		siting?: SaunaSiting;
		indoorRoom?: IndoorRoomConfig;
		wallHeightM?: number;
		overrides?: BuildInput["overrides"];
	},
	ctx: SquareBaseBuildContext,
	/**
	 * Required for a correct `interior` rebuild: without it a layout edit would
	 * rebuild the interior group from the GENERATED default and silently throw
	 * the user's benches away — a selective rebuild that undoes the very change
	 * that triggered it.
	 */
	interior?: InteriorRenderPlan,
): THREE.Group {
	const group = new THREE.Group();
	// Must skip exactly what `buildSauna3D` skips, or an edit that rebuilds one
	// subsystem would put the facade back on an indoor sauna.
	const indoor = input.siting === "indoor";
	const heater: HeaterRef = {
		heaterType: input.thermalInputs?.heaterType ?? "wood",
		heaterModelId: input.thermalInputs?.heaterModelId,
	};

	switch (subsystem) {
		case DirtyFlag.Foundation:
			if (indoor) return group;
			buildFoundation(
				ctx,
				calculateFoundation(
					input.dimensions.width,
					input.dimensions.length,
					input.siteConditions,
				),
				group,
				{
					namePrefix: "square",
					includeEntrySteps: true,
					entryCenterXM: getFrontDoorCenterX(ctx, input.openings),
					siteConditions: input.siteConditions,
					deckConfig: input.deckConfig,
				},
			);
			return group;
		case DirtyFlag.Deck:
			return group;
		case DirtyFlag.Roof:
			buildRoof(ctx, group, createFrontVisibilityGroup("roof", group), {
				namePrefix: "square",
				style: indoor ? "ceiling" : "sloped-detailed",
				flue: squareFlueAxis(
					heater,
					resolveSquareInteriorPlan(
						ctx,
						heater,
						input.thermalInputs?.heaterPlacementZMm,
						interior,
					),
				),
			});
			return group;
		case DirtyFlag.Interior: {
			const framingResults = buildWallFraming(
				ctx,
				input.openings,
				new THREE.Group(),
				new THREE.Group(),
				{ namePrefix: "square" },
			);
			buildSquareInterior(
				ctx,
				group,
				createFrontVisibilityGroup("interior", group),
				framingResults,
				heater,
				input.thermalInputs?.heaterPlacementZMm,
				interior,
				input.finishes?.panelDirection,
				input,
			);
			return group;
		}
		case DirtyFlag.Framing:
			buildFloorJoists(ctx, group, new THREE.Group(), { namePrefix: "square" });
			buildWallFraming(
				ctx,
				input.openings,
				group,
				createFrontVisibilityGroup("framing", group),
				{ namePrefix: "square" },
			);
			return group;
		case DirtyFlag.Panel: {
			const framingResults = buildWallFraming(
				ctx,
				input.openings,
				new THREE.Group(),
				new THREE.Group(),
				{ namePrefix: "square" },
			);
			buildFloorJoists(ctx, new THREE.Group(), group, { namePrefix: "square" });
			const frontPanelGroup = createFrontVisibilityGroup("panel", group);
			buildPanels(ctx, framingResults, group, frontPanelGroup, {
				namePrefix: "square",
				mode: "striped",
				panelDirection: input.finishes?.panelDirection,
			});
			buildInteriorCeilingFinish(ctx, group, "square", frontPanelGroup);
			return group;
		}
		case DirtyFlag.Exterior: {
			const framingResults = buildWallFraming(
				ctx,
				input.openings,
				new THREE.Group(),
				new THREE.Group(),
				{ namePrefix: "square" },
			);
			const frontExteriorGroup = createFrontVisibilityGroup("exterior", group);
			if (indoor) {
				buildIndoorOuterLayers(ctx, framingResults, group, frontExteriorGroup, {
					namePrefix: "square",
					existingWalls: normalizeIndoorRoom(input.indoorRoom).existingWalls,
				});
				addPremiumFrontDoorFallbacks(
					ctx,
					framingResults as Map<string, WallFramingResult>,
					frontExteriorGroup,
				);
				return group;
			}
			const { wbThick } = buildExteriorFacade(
				ctx,
				framingResults,
				group,
				frontExteriorGroup,
				{
					namePrefix: "square",
					mode: "ventilated",
					baseTrimSuppressedWalls: getDeckSuppressedWalls(input.deckConfig),
				},
			);
			addPremiumFrontDoorFallbacks(
				ctx,
				framingResults as Map<string, WallFramingResult>,
				frontExteriorGroup,
			);
			buildSquareExteriorVents(ctx, group, frontExteriorGroup, wbThick);
			return group;
		}
		default:
			return group;
	}
}

/**
 * Rows an indoor sauna (issue 156) does not buy: the ground foundation under
 * the building and everything on the outside of its walls. Keyed on
 * `materialKey`, which `buildSauna3D` skips in step — no plintar, stolpskor or
 * bärlinor are drawn, and no facade layer exists to clad or paint.
 */
/** Issue 174: the indoor sauna's outside, seen from the room. */
export const INDOOR_OUTER_PANEL_KEY = "indoor.outer-panel";
export const INDOOR_FIBREBOARD_KEY = "indoor.fibreboard";

const OUTDOOR_ONLY_KEYS = new Set([
	"foundation.plinth",
	"foundation.post-shoe",
	"foundation.ground-fabric",
	"foundation.drainage-gravel",
	"foundation.beam",
	"systems.surface-treatment",
]);

function isOutdoorOnlyKey(materialKey: string | undefined): boolean {
	if (!materialKey) return false;
	return (
		OUTDOOR_ONLY_KEYS.has(materialKey) || materialKey.startsWith("exterior.")
	);
}

export function calculateMaterials(
	W: number,
	L: number,
	roofConfig: RoofConfig = DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig,
	siteConditions: SiteConditions = DEFAULT_SITE_CONDITIONS,
	heaterType: HeaterType = "wood",
	roofJoists?: RoofJoistResult,
	/** See `RawSaunaEngine.calculateMaterials`. */
	bench?: BenchMaterialsResult,
	/**
	 * Only `finishes.panelDirection` reaches this function — species and
	 * exterior colour are stamped onto the finished rows by the enrichment
	 * chain, because they are pure renames/reprices that every engine shares.
	 * Absent means the defaults, i.e. the pre-issue-134 BOM.
	 */
	opts: Pick<
		SquareBuildOptions,
		"finishes" | "wallHeightM" | "siting" | "indoorRoom"
	> = {},
): RawCalculationResult {
	const finishes = opts.finishes ?? DEFAULT_FINISHES;
	const indoor = opts.siting === "indoor";
	const H = resolveWallHeightM(opts.wallHeightM);
	const structuralRoofJoists = getStructuralRoofJoists(roofJoists);
	const roofGeometry = createRectRoofGeometry({
		width: W,
		length: L,
		wallHeight: H,
		roofConfig,
	});
	const pitchRad = roofGeometry.pitchRad;
	const roofRise = roofGeometry.roofRise;
	// Mono fall: along the length (front/back high wall, the historical shape)
	// or across the width (left/right high wall). The run is the dimension
	// the roof rises over; the raked walls' hammarband is that run along the
	// slope, the two eave walls stay level.
	const monoAcross = roofGeometry.monoSlope?.axis === "x";
	const monoRunL = roofGeometry.monoSlope?.runLength ?? L;
	const monoEaveL = roofGeometry.monoSlope?.eaveLength ?? W;
	const monoSlopedL = monoRunL / Math.cos(Math.max(pitchRad, 0.0001));
	const gableHalfSlopedW = W / 2 / Math.cos(Math.max(pitchRad, 0.0001));
	// addSlopedBox shears the horizontal run — the renderer adds its 0.2 m
	// overhang to the HORIZONTAL half-width before shearing, so the along-
	// slope rafter overhang is 0.2/cos(pitch), not a flat 0.2 m tacked onto
	// the already-projected wall-line run. Use this (not
	// `gableHalfSlopedW + 0.2`) for anything measuring rafter/covering
	// length past the eave.
	const gableHalfSlopedWWithOverhang =
		(W / 2 + 0.2) / Math.cos(Math.max(pitchRad, 0.0001));
	const averageWallHeight = (wall: "front" | "back" | "left" | "right") =>
		(roofGeometry.getMinWallHeight(wall) +
			roofGeometry.getMaxWallHeight(wall)) /
		2;
	const leftWallAverageHeight = averageWallHeight("left");
	const rightWallAverageHeight = averageWallHeight("right");

	const numFloorJoists = Math.ceil((W - 0.045) / CC) + 1;
	// Trossbottenläkt: one läkt per joist SIDE of every bay, full building
	// length. Mirrors addSubfloorBays, which draws exactly two per bay.
	const lmSubfloorLedger = Math.ceil(resolveSubfloorLedgerLm(W, L, 0.045, CC));
	const numSubfloorLedgers = 2 * resolveSubfloorBayCount(W, 0.045, CC);
	const numRoofJoists = Math.ceil((L - 0.045) / CC) + 1;
	const totalRoofJoists = numRoofJoists;
	// Approximate stud lines: calculateMaterials has no openings input, so the
	// extra king/jack studs the renderer draws at opening edges aren't counted
	// here — only the plain c/c grid `generateFramedWall` lays on a blank wall.
	// The two-room engine, which receives openings, passes the real deduped
	// stud axes per wall instead (see calculateTwoRoomFramingPlan).
	const studLinesByWall = defaultStudLinesByWall(W, L);

	// Inv. spikläkt / glespanel. Korsläkt: the battens cross the panel, so the
	// DEFAULT (standing panel) buys LYING rows — rows × wall length, not
	// studCount × height (which counted a standing batten layer the renderer
	// no longer — and never should have — drawn). Choosing lying panel flips
	// it, and the alternate figure rides along so the UI can price the swatch.
	// Shared with the audit evidence row via interiorQuantities.ts.
	const { lmInteriorBattens, lmInteriorBattensAlternate } =
		calculateInteriorLiningQuantities({
			W,
			L,
			roofGeometry,
			panelDirection: finishes.panelDirection,
			studLinesByWall,
		});

	// Gavelfyllnadsreglar: short vertical studs in the gable triangle above hammarband
	const numGableInfillStuds =
		roofConfig.kind === "gable"
			? Math.max(0, Math.ceil((W - 0.045) / CC) - 1)
			: 0;
	const gableInfillTotalLm =
		roofConfig.kind === "gable"
			? numGableInfillStuds * (roofRise / 2) * 2 // average height × 2 gable walls
			: 0;

	const area = W * L;
	const ceilingArea =
		roofConfig.kind === "gable"
			? 2 * gableHalfSlopedW * L
			: monoSlopedL * monoEaveL;
	const wallArea =
		roofConfig.kind === "gable"
			? 2 * W * H + W * roofRise + 2 * L * H
			: W * averageWallHeight("front") +
				W * averageWallHeight("back") +
				L * leftWallAverageHeight +
				L * rightWallAverageHeight;
	const lmWallPanel = Math.ceil(wallArea * 11.5);
	const lmCeilingPanel = Math.ceil(ceilingArea * 11.5 * 1.1);
	const lmCeilingBattens = Math.ceil(numRoofJoists * W);
	const lmCeilingTrim = Math.ceil(2 * W + 2 * L);
	// Bench timber is no longer a width heuristic (`ceil((W − 0.2) × 14)`): it
	// comes from the interior layout's actual runs — see deriveBenchMaterials.
	const lmLavar = Math.ceil((bench?.slatLpm ?? 0) + (bench?.skirtLpm ?? 0));
	const lmFloorBoards = Math.ceil(area * 8.5);

	const foundation = calculateFoundation(W, L, siteConditions);
	const lmTreatedBeams = Math.ceil(foundation.totalBeamLengthLm * 1.05);
	// Gable: buildRoof draws TWO sloped rafters (left + right fall) at every
	// joist position — one half-slope length per position under-bought ~30%.
	// Indoors they are ceiling joists cut to the wall line — `buildRoof`'s
	// "ceiling" style draws them W long, with no eave overhang to buy.
	const roofJoistLm = indoor
		? totalRoofJoists * W
		: roofConfig.kind === "gable"
			? totalRoofJoists * 2 * gableHalfSlopedWWithOverhang
			: monoAcross
				? // Rafters sloped across the width, overhang both eaves.
					Math.ceil((L - 0.045) / CC + 1) *
					((W + 0.4) / Math.cos(Math.max(pitchRad, 0.0001)))
				: Math.ceil((L - 0.045) / CC + 1) * (W + 0.4);
	const lmFloorJoists = Math.ceil(numFloorJoists * L);
	const lmRoofJoists = Math.ceil(roofJoistLm);
	const standardWallCuts = calculateWallStudCuts({
		dimensions: { width: W, length: L },
		roofConfig,
		openings: [],
		wallHeightM: H,
	});
	const standardStudLm = standardWallCuts
		.flatMap((c) => c.pieces ?? [])
		.reduce((sum, p) => sum + (p.lengthMm * p.count) / 1000, 0);
	// Plates: syll + hammarband per wall. The raked walls' hammarband runs up
	// the slope — the sides under a front-high mono, the front/back under a
	// mono falling across the width.
	const lmWallStuds = monoAcross
		? Math.ceil(
				standardStudLm +
					gableInfillTotalLm +
					W * 2 +
					monoSlopedL * 2 +
					(L - 0.19) * 4,
			)
		: Math.ceil(
				standardStudLm +
					gableInfillTotalLm +
					W * 4 +
					(roofConfig.kind === "gable"
						? (L - 0.19) * 4
						: (L - 0.19) * 2 + (monoSlopedL - 0.19) * 2),
			);

	const wasteMargin = 1.1;
	const lmExteriorCladding = Math.ceil((wallArea / CLAD_COV) * wasteMargin);
	// Issue 174: indoors, walls against the room's wall get a träfiberskiva,
	// free-standing walls a panel straight on the studs — per wall, the same
	// split `buildIndoorOuterLayers` draws.
	const indoorRoom = normalizeIndoorRoom(opts.indoorRoom);
	const wallAreaOf = (wall: "front" | "back" | "left" | "right") =>
		(wall === "front" || wall === "back" ? W : L) * averageWallHeight(wall);
	const existingWallArea = indoorRoom.existingWalls.reduce(
		(sum, wall) => sum + wallAreaOf(wall),
		0,
	);
	const freeWallArea = freeWalls(indoorRoom).reduce(
		(sum, wall) => sum + wallAreaOf(wall),
		0,
	);
	const lmIndoorOuterPanel = Math.ceil((freeWallArea / CLAD_COV) * wasteMargin);
	const indoorRows: MaterialRow[] = indoor
		? [
				...(freeWallArea > 0
					? [
							{
								material: "Ytterpanel (Spontad Gran), fristående väggar",
								dimension: "22×120 mm (110mm täck)",
								quantity: `${lmIndoorOuterPanel} lpm (Inkl. 10% spill)`,
								phase: "interior" as const,
								priceRuleId: "prices.ytterpanel",
								materialKey: INDOOR_OUTER_PANEL_KEY,
							},
						]
					: []),
				...(existingWallArea > 0
					? [
							{
								material: "Träfiberskiva mot befintlig vägg",
								dimension: "12 mm skiva, 20 mm luftspalt",
								quantity: `${Math.ceil(existingWallArea)} m²`,
								phase: "interior" as const,
								priceRuleId: "prices.vindskydd",
								materialKey: INDOOR_FIBREBOARD_KEY,
							},
						]
					: []),
			]
		: [];
	// Ventilated facade layer stack (vindskydd → korsläkt → panel) — shared
	// BOM math so the quantities always mirror what buildExteriorFacade draws.
	const {
		lmStroToLakt,
		lmHorizontalFurring,
		windBarrierArea,
		cornerTrimLm,
		lmBaseTrim,
		baseTrimCutFrontBackM,
		baseTrimCutSideM,
	} = calculateVentilatedFacadeQuantities({
		W,
		L,
		studLinesByWall,
		wallArea,
		roofGeometry,
	});
	const fasciaLm =
		roofConfig.kind === "gable"
			? Math.ceil(
					2 * (L + 0.4) +
						4 * ((W / 2 + 0.2) / Math.cos(Math.max(pitchRad, 0.0001))),
				)
			: Math.ceil(
					2 * (monoEaveL + 0.444) +
						2 * ((monoRunL + 0.4) / Math.cos(Math.max(pitchRad, 0.0001))),
				);
	const foundationArea = Math.ceil((W + 0.4) * (L + 0.4));
	const foilTapePackages = Math.max(
		1,
		Math.ceil((wallArea + ceilingArea) / 25),
	);
	const fastenerPackages = Math.max(
		2,
		Math.ceil((lmWallStuds + lmFloorJoists + lmRoofJoists) / 120),
	);
	const finishPackages = Math.max(
		1,
		Math.ceil((lmExteriorCladding + lmWallPanel + lmCeilingPanel) / 400),
	);
	const heaterRows: MaterialRow[] =
		heaterType === "electric"
			? [
					{
						material: "Elaggregat",
						dimension: "Mellansegment",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.elaggregat",
						materialKey: "heater.electric",
					},
					{
						material: "Aggregatsten",
						dimension: "20 kg",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.aggregatsten",
						materialKey: "heater.stones",
					},
					{
						material: "Styrpanel",
						dimension: "Extern/integrerad",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.styrpanel",
						materialKey: "heater.control-panel",
					},
				]
			: [
					{
						material: "Bastuaggregat",
						dimension: "Vedeldad kamin",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.bastuaggregat",
						materialKey: "heater.wood",
					},
				];

	const materials: MaterialRow[] = [
		{
			material: `Betongplint ${foundation.selectedPlinthLengthMm}mm`,
			dimension: "Ø160mm",
			quantity: `${foundation.totalPiers} st`,
			phase: "foundation",
			priceRuleId: "prices.betongplint",
			materialKey: "foundation.plinth",
		},
		{
			material: "Stolpsko / infästning",
			dimension: "Grundinfästning",
			quantity: `${foundation.totalPiers} st`,
			phase: "foundation",
			priceRuleId: "prices.stolpsko",
			materialKey: "foundation.post-shoe",
		},
		{
			material: "Markduk",
			dimension: "Geotextil",
			quantity: `${foundationArea} m²`,
			phase: "foundation",
			priceRuleId: "prices.markduk",
			materialKey: "foundation.ground-fabric",
		},
		{
			material: "Dräneringsgrus / makadam",
			dimension: "Grundlager",
			quantity: `${foundationArea} m²`,
			phase: "foundation",
			priceRuleId: "prices.draneringsgrus",
			materialKey: "foundation.drainage-gravel",
		},
		{
			material: "Bärlina Tryckimpregnerad",
			dimension: "45×145 mm (NTR-A)",
			quantity: `${lmTreatedBeams} lpm`,
			phase: "foundation",
			priceRuleId: "prices.barlina",
			materialKey: "foundation.beam",
		},
		{
			material: "Golvbjälkar (K-virke)",
			dimension: getFloorJoistDimensionLabel(),
			quantity: `${lmFloorJoists} lpm`,
			phase: "framing",
			priceRuleId: getFloorJoistPriceRuleId(),
			materialKey: "framing.floor-joists",
		},
		{
			material: getRoofJoistMaterialName(structuralRoofJoists),
			dimension: getRoofJoistDimensionLabel(structuralRoofJoists),
			quantity: `${lmRoofJoists} lpm`,
			phase: "framing",
			priceRuleId: getRoofJoistPriceRuleId(structuralRoofJoists),
			materialKey: "framing.roof-joists",
		},
		{
			material: "Väggreglar, Syll, Hammarband",
			dimension: "45×95 mm",
			quantity: `${lmWallStuds} lpm`,
			phase: "framing",
			priceRuleId: "prices.vaggreglar",
			materialKey: "framing.wall-studs",
		},
		{
			material: "Stenullsisolering",
			// The floor bay is joistHeight minus läkt + trossbottenskiva, so the
			// floor batt is a size DOWN from the joist — 145 mm does not fit.
			dimension: `Vägg 95mm / Tak ${getRoofJoistDepthMm(structuralRoofJoists)}mm / Golv ${resolveSubfloorBattThicknessMm(JOIST_H)}mm`,
			quantity: `Vägg: ${Math.ceil(wallArea)}m² / Tak: ${Math.ceil(ceilingArea)}m² / Golv: ${Math.ceil(area)}m²`,
			phase: "interior",
			priceRuleId: "prices.stenullsisolering",
			materialKey: "interior.insulation",
		},
		{
			material: "Åldersbeständig Bastufolie",
			dimension: "Aluminium",
			quantity: `${Math.ceil(wallArea + ceilingArea)} m²`,
			phase: "interior",
			priceRuleId: "prices.bastufolie",
			materialKey: "interior.vapor-barrier",
		},
		{
			material: "Alutejp / fogtejp",
			dimension: "50 mm",
			quantity: `${foilTapePackages} paket`,
			phase: "interior",
			priceRuleId: "prices.alutejp",
			materialKey: "interior.foil-tape",
		},
		{
			material: "Inv. Spikläkt / Glespanel",
			dimension: "22×45 mm",
			quantity: `${lmInteriorBattens} lpm`,
			phase: "interior",
			priceRuleId: "prices.spiklakt",
			materialKey: "interior.batten",
		},
		{
			material: "Takgles / Spikläkt (Innertak)",
			dimension: "22×45 mm",
			quantity: `${lmCeilingBattens} lpm`,
			phase: "interior",
			priceRuleId: "prices.spiklakt",
			materialKey: "interior.ceiling-batten",
		},
		{
			// Base name only — `withInteriorWood` appends the chosen species.
			// It used to read "Bastupanel (t.ex. Gran/Asp)", a hedge that was
			// priced as gran while the viewer rendered aspen; the species is a
			// real choice now, so the row states one.
			material: "Bastupanel",
			dimension: "15×90 mm",
			quantity: `${lmWallPanel} lpm (Inkl. 10% spill)`,
			phase: "interior",
			priceRuleId: "prices.bastupanel",
			materialKey: "interior.sauna-panel",
		},
		{
			material: "Bastupanel (Innertak)",
			dimension: "15×90 mm",
			quantity: `${lmCeilingPanel} lpm (Inkl. 10% spill)`,
			phase: "interior",
			priceRuleId: "prices.bastupanel",
			materialKey: "interior.ceiling-panel",
		},
		{
			material: "Taklist / Bomlist",
			dimension: "22×45 mm",
			quantity: `${lmCeilingTrim} lpm`,
			phase: "interior",
			priceRuleId: "prices.fonsterfoder",
			materialKey: "interior.ceiling-trim",
		},
		// Lavar: layout-backed rows (slats, bärlina, front board, frame, screws).
		...(bench?.materials ?? []),
		{
			// Trossbottenskiva: vindtät men diffusionsöppen asfaboard vilande på
			// trossbottenläkt inne i bjälkfacken — INTE en andra golvbrädskiva.
			// Full golvyta (inte yta minus bjälkarnas ~7,5 %): skivorna säljs som
			// hela plattor och kapas i fackbredder, och remsorna som blir över vid
			// bjälklinjerna är inte återanvändbara.
			material: "Undergolv / Trossbotten",
			dimension: "12 mm asfaboard",
			quantity: `${Math.ceil(area)} m²`,
			phase: "foundation",
			priceRuleId: "prices.undergolv",
			materialKey: "foundation.subfloor",
		},
		{
			material: "Trossbottenläkt",
			dimension: "28×70 mm",
			quantity: `${lmSubfloorLedger} lpm`,
			phase: "foundation",
			priceRuleId: "prices.trossbottenlakt",
			materialKey: "foundation.subfloor-ledger",
		},
		{
			material: "Fukttåligt golv / Trall",
			dimension: "28×120 mm",
			quantity: `${Math.ceil(area)} m²`,
			phase: "interior",
			priceRuleId: "prices.fukttaligtGolv",
			materialKey: "interior.floor",
		},
		{
			material: "Vindskydd (skiva/duk)",
			dimension: "Vindskyddsskiva / diffusionsöppen duk",
			quantity: `${windBarrierArea} m²`,
			phase: "roofExterior",
			priceRuleId: "prices.vindskydd",
			materialKey: "exterior.wind-barrier",
		},
		{
			material: "Ytterpanel (Spontad Gran)",
			dimension: "22×120 mm (110mm täck)",
			quantity: `${lmExteriorCladding} lpm (Inkl. 10% spill)`,
			phase: "roofExterior",
			priceRuleId: "prices.ytterpanel",
			materialKey: "exterior.cladding",
		},
		{
			material: "Utv. Ströläkt (Luftspalt)",
			dimension: "22×45 mm",
			quantity: `${lmStroToLakt} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.spiklakt",
			materialKey: "exterior.furring",
		},
		{
			material: "Utv. Spikläkt (Liggande)",
			dimension: "22×45 mm",
			quantity: `${lmHorizontalFurring} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.spiklakt",
			materialKey: "exterior.furring-horizontal",
		},
		{
			material: "Knutbrädor",
			dimension: "22×90 mm",
			quantity: `${cornerTrimLm} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.knutbrador",
			materialKey: "exterior.trim-boards",
		},
		{
			material: "Nederkantsavslut",
			dimension: "22×95 mm",
			quantity: `${lmBaseTrim} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.nederkantsavslut",
			materialKey: "exterior.drip-edge",
		},
		{
			material: "Vindskivor (Fascia)",
			dimension: "22×145 mm",
			quantity: `${fasciaLm} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.vindskivor",
			materialKey: "exterior.fascia",
		},

		{
			material: "Bastudörr",
			dimension: "Härdat glas / träknopp",
			quantity: "1 st",
			phase: "systems",
			priceRuleId: "prices.bastuDorr",
			materialKey: "systems.door.sauna",
		},
		{
			material: "Ventiler",
			dimension: "Tilluft + frånluft",
			quantity: "2 st",
			phase: "systems",
			priceRuleId: "prices.ventiler",
			materialKey: "systems.ventilation",
		},
		{
			material: "Rostfri skruv / beslagspaket",
			dimension: "Stomme och invändig infästning",
			quantity: `${fastenerPackages} paket`,
			phase: "systems",
			priceRuleId: "prices.rostfriSkruv",
			materialKey: "systems.fasteners",
		},
		{
			material: "Ytbehandling / färg / olja",
			dimension: "Fasad och utsatta träytor",
			quantity: `${finishPackages} paket`,
			phase: "systems",
			priceRuleId: "prices.ytbehandling",
			materialKey: "systems.surface-treatment",
		},
		...heaterRows,
	];

	const cuts: CutRow[] = [
		{
			part: "Bärlinor (Grund, NTR-A 45×145)",
			count: `${foundation.numBeams} st`,
			cutLength: `${((W - 0.05) * 1000).toFixed(0)} mm`,
			materialKey: "foundation.beam",
		},
		{
			part: "Golvbjälkar (45×145)",
			count: `${numFloorJoists} st`,
			cutLength: `${(L * 1000).toFixed(0)} mm`,
			materialKey: "framing.floor-joist",
		},
		{
			part: `Takbjälkar (${getRoofJoistCutDimensionLabel(structuralRoofJoists)})`,
			count:
				roofConfig.kind === "gable"
					? `${totalRoofJoists * 2} st (inkl. 2 gaveltakstolar)`
					: `${totalRoofJoists} st`,
			cutLength: indoor
				? `${(W * 1000).toFixed(0)} mm (innertaksbjälke, inget överhäng)`
				: roofConfig.kind === "gable"
					? `${(gableHalfSlopedWWithOverhang * 1000).toFixed(0)} mm per takfall`
					: monoAcross
						? `${(((W + 0.4) / Math.cos(Math.max(pitchRad, 0.0001))) * 1000).toFixed(0)} mm (Inkl. överhäng, ${roofConfig.pitchDeg}° snitt)`
						: `${((W + 0.4) * 1000).toFixed(0)} mm (Inkl. överhäng)`,
			...getRoofJoistCutPurchase(structuralRoofJoists),
			materialKey: "framing.roof-joist",
		},
		{
			part: monoAcross
				? "Syll / Hammarband (Fram/Bak)"
				: "Syll / Hammarband (Ena ledden, Plan)",
			count: "4 st",
			// A mono falling across the width rakes the front/back walls.
			cutLength: monoAcross
				? `2 × ${(W * 1000).toFixed(0)} mm syll, 2 × ${(monoSlopedL * 1000).toFixed(0)} mm hammarband (${roofConfig.pitchDeg}°)`
				: `${(W * 1000).toFixed(0)} mm`,
			materialKey: "framing.plates",
		},
		{
			part: "Syllbotten (Sidor, Plan)",
			count: "2 st",
			cutLength: `${((L - 0.09) * 1000).toFixed(0)} mm`,
			materialKey: "framing.sill-sides",
		},
		{
			part: "Hammarband",
			count: roofConfig.kind === "gable" ? "4 st" : "2 st",
			cutLength:
				roofConfig.kind === "gable"
					? `${((L - 0.09) * 1000).toFixed(0)} mm (gavelsidor plana)`
					: monoAcross
						? `${((L - 0.09) * 1000).toFixed(0)} mm (takfotssidor plana)`
						: `${((monoSlopedL - 0.09) * 1000).toFixed(0)} mm (${roofConfig.pitchDeg}° snitt)`,
			materialKey: "framing.top-plate",
		},
		...standardWallCuts,
		{
			part: "Vindskivor",
			count: roofConfig.kind === "gable" ? "4 st" : "2 st",
			cutLength:
				roofConfig.kind === "gable"
					? `${(gableHalfSlopedWWithOverhang * 1000).toFixed(0)} mm (${roofConfig.pitchDeg}° snitt)`
					: `${(((monoRunL + 0.4) / Math.cos(Math.max(pitchRad, 0.0001))) * 1000).toFixed(0)} mm (${roofConfig.pitchDeg}° snitt)`,
			materialKey: "exterior.fascia",
		},
		{
			part: monoAcross
				? "Vindskivor (Vänster/Höger: 22x145)"
				: "Vindskivor (Fram/Bak: 22x145)",
			count: roofConfig.kind === "gable" ? "4 st" : "2 st",
			cutLength: `${(((roofConfig.kind === "gable" ? W : monoEaveL) + 0.444) * 1000).toFixed(0)} mm`,
			materialKey: "exterior.fascia-frontback",
		},
		{
			part: "Ytterpanel (Fasad)",
			count: `${lmExteriorCladding} lpm`,
			cutLength:
				roofConfig.kind === "gable"
					? `Kapas på plats (gavlar följer nocktak ${roofConfig.pitchDeg}°)`
					: `Kapas på plats (sidor trapetskapas och front byggs upp för pulpettak ${roofConfig.pitchDeg}°)`,
			materialKey: "exterior.cladding",
		},
		buildInteriorPanelCutRow({
			lmWallPanel,
			wallHeightM: H,
			// The tallest standing run anywhere on the building — gable ridge or
			// mono high eave. Without it every board is quoted at the flat wall
			// height, and everything above the eave is cut short.
			maxWallHeightM: Math.max(
				...(["front", "back", "left", "right"] as const).map((wall) =>
					roofGeometry.getMaxWallHeight(wall),
				),
			),
			widthM: W,
			lengthM: L,
			finishes,
		}),
		{
			part: "Innertakspanel",
			count: `${Math.ceil(lmCeilingPanel / Math.max(L, 1))} st`,
			cutLength: `Kapas på plats mot ${roofConfig.kind === "gable" ? "takfall / nock" : "taklutning"} (${roofConfig.pitchDeg}°)`,
			materialKey: "interior.ceiling-panel",
		},
		{
			part: "Taklist / Bomlist",
			count: "1 sats",
			cutLength: `Löpande mot vägg/takvinkel, cirka ${lmCeilingTrim} lpm`,
			materialKey: "interior.ceiling-trim",
		},
		...(roofConfig.kind === "gable"
			? [
					{
						part: "Gavelfyllnadsreglar (45×95)",
						count: `${numGableInfillStuds * 2} st`,
						cutLength: `Kapas stigande per regel, snedkapas ${roofConfig.pitchDeg}° mot nocken`,
						materialKey: "framing.gable-infill",
					},
				]
			: []),
		{
			part: "Knutbrädor",
			count: "8 st",
			cutLength: `Kapas på plats mot taklutning (${roofConfig.pitchDeg}°)`,
			materialKey: "exterior.trim-boards",
		},
		{
			part: "Nederkantsavslut",
			count: "4 st",
			cutLength: `2 × ${(baseTrimCutFrontBackM * 1000).toFixed(0)} mm, 2 × ${(baseTrimCutSideM * 1000).toFixed(0)} mm`,
			materialKey: "exterior.drip-edge",
		},
		{
			part: "Undergolv / Trossbotten",
			count: "1 sats",
			cutLength: `Skivkapas på plats till ${Math.ceil(area)} m²`,
			materialKey: "foundation.subfloor",
		},
		{
			part: "Trossbottenläkt (28×70)",
			count: `${numSubfloorLedgers} st`,
			cutLength: `${(L * 1000).toFixed(0)} mm`,
			materialKey: "foundation.subfloor-ledger",
			purchaseDimension: "28x70",
		},
	];

	const weight = calculateWeight({
		lmWallStuds,
		lmFloorJoists,
		lmRoofJoists,
		roofJoistDepthMm: getRoofJoistDepthMm(structuralRoofJoists),
		lmPanel: lmWallPanel + lmCeilingPanel,
		lmLavar,
		// The ledger is one rail per run; the front board is the other, and it
		// shares the 45×70 section with the frame and legs.
		lmBenchLedger: bench?.ledgerLpm ?? 0,
		lmBenchFrame: (bench?.railLpm ?? 0) + (bench?.frameLpm ?? 0),
		lmFloorBoards,
		lmTreatedBeams: indoor ? 0 : lmTreatedBeams,
		totalWallAreaSqm: Math.ceil(wallArea),
		totalRoofAreaSqm: indoor ? 0 : Math.ceil(area),
		numPlinths: indoor ? 0 : foundation.totalPiers,
		lmExteriorCladding: indoor ? lmIndoorOuterPanel : lmExteriorCladding,
		lmFurringStrips: indoor ? 0 : lmStroToLakt + lmHorizontalFurring,
	});

	cuts.push(...(bench?.cuts ?? []));

	return {
		materials: indoor
			? [
					...materials.filter((row) => !isOutdoorOnlyKey(row.materialKey)),
					...indoorRows,
				]
			: materials,
		cuts: indoor
			? [
					...cuts.filter((row) => !isOutdoorOnlyKey(row.materialKey)),
					...(freeWallArea > 0
						? [
								{
									part: "Ytterpanel (fristående väggar)",
									count: `${lmIndoorOuterPanel} lpm`,
									cutLength: "Kapas på plats",
									materialKey: INDOOR_OUTER_PANEL_KEY,
								},
							]
						: []),
				]
			: cuts,
		// Issue 175: indoors there are no plinths — without this the guide's
		// kickoff, the Grund tab and the shared summary all quoted 9 plintar.
		...(indoor ? {} : { foundation }),
		weight,
		structuralRoofJoists,
		finishBasis: {
			// The same wall area the vindskydd row is bought from — the facade
			// treatment covers exactly what the cladding covers.
			facadeAreaSqm: indoor ? 0 : wallArea,
			selectedBattenLpm: lmInteriorBattens,
			alternateBattenLpm: lmInteriorBattensAlternate,
		},
	};
}

export function getDefaultCameraPosition(
	W: number,
	L: number,
): { position: THREE.Vector3; target: THREE.Vector3 } {
	const ctx = createSquareBuildContext(W, L);
	return createCameraPosition(
		{
			width: W + 0.44,
			height: ctx.wallBase + ctx.ridgeHeight + 0.16,
			length: L + 0.4,
		},
		[0.56, 0.28, 0.78],
		1.45,
		[0, 0.37, 0.02],
	);
}

export function getSquareCameraPresets(
	W: number,
	L: number,
	/**
	 * Plan-view centre of the lavar, from the resolved layout. The interior
	 * preset used to aim at a hard-coded fraction of the building, which points
	 * away from the benches as soon as a user moves them.
	 */
	benchFocusM?: { xM: number; zM: number } | null,
) {
	const ctx = createSquareBuildContext(W, L);
	const size = {
		width: W + 0.44,
		height: ctx.wallBase + ctx.ridgeHeight + 0.16,
		length: L + 0.4,
	};

	return [
		createCameraPreset(size, {
			id: "hero",
			label: "Hero",
			direction: [0.52, 0.2, 0.83],
			distanceMultiplier: 1.53,
			targetOffset: [0, 0.45, 0.04],
		}),
		createCameraPreset(size, {
			id: "side",
			label: "Sida",
			direction: [0.98, 0.2, 0.03],
			distanceMultiplier: 1.16,
			targetOffset: [0, 0.42, 0],
		}),
		createCameraPreset(size, {
			id: "interior-peek",
			label: "Interiör",
			direction: [-0.32, 0.23, 0.92],
			distanceMultiplier: 1.1,
			targetOffset: [0, 0.47, -0.08],
			// Height stays a fraction of the building (it frames the room, not the
			// seat); only where to LOOK comes from the benches.
			targetPlanM: benchFocusM ?? undefined,
		}),
	];
}
